import { MouseEvent as ReactMouseEvent, useState } from "react";

import { AxiosError } from "axios";
import _get from "lodash/get";
import { useRouter } from "next/router";

import PrimaryButton from "components/button/primaryButton/PrimaryButton";
import Drawers from "components/drawers/Drawers";
import { GenericError } from "components/errorBoundary/ErrorBoundary";
import SchemaFormModal from "components/modal/schemaFormModal/SchemaFormModal";
import Pages from "components/preview/pages/Pages";
import { useSubtotal } from "hooks/useSubtotal";
import { FormDocument } from "prismicio-types";
import { useUpdateCartItem } from "queries/legacy/cart-queries";
import { useUpdateOrderItem } from "queries/legacy/order-queries";
import {
  useGetToc,
  useGetProduct,
  useGetCustomisation,
  useSetCustomisation,
  useGetSchema,
} from "queries/legacy/product-queries";
import { useGetCsrfToken } from "queries/legacy/website-queries";
import { pageview } from "tracking/gtm";
import { TocError, UseGetProductResponse } from "types/queries";
import {
  MISSING_ESSENTIAL_DATA_ERROR,
  SSR_CONFIGURATION_ERROR,
} from "utils/error-utils";
import { getUpsellPriceFromProduct } from "utils/extras-utils";
import { CANVAS_FALLBACK_CONTENT } from "utils/fallback-content";
import { useReCropPhotosOnSchemaChange } from "utils/photo-utils";
import { getLocaleFromMarketplace, useInterpolate } from "utils/prismic-utils";
import { useFindByKey } from "utils/schema-utils";
import { useGetPathnameForDestination } from "utils/url-utils";

import styles from "styles/pages/Preview.module.scss";

import PreviewSkeleton from "./skeleton/PreviewSkeleton";

export const getPageUpsellPrice = (
  data: qs.ParsedQs,
  product: UseGetProductResponse
) => {
  const [[customisationName, customisationValue]] = Object.entries(data);

  const price = getUpsellPriceFromProduct(
    customisationName,
    customisationValue as string,
    product as UseGetProductResponse
  );

  if (price === null) {
    return null;
  }

  return { [customisationName]: Number(price?.toFixed(2)) };
};

export default function PreviewComponent() {
  const router = useRouter();

  const { data: product } = useGetProduct();
  const { updateSubtotal } = useSubtotal(product?.museSlug);

  const { isLoading: schemaIsLoading } = useGetSchema();
  const { data: toc, error: tocError } = useGetToc();
  const { data: customisation, error: customisationError } =
    useGetCustomisation();
  const { data: csrfToken } = useGetCsrfToken();

  const { mutate: setCustomisation } = useSetCustomisation();

  const getPathnameForDestination = useGetPathnameForDestination();
  const interpolate = useInterpolate();
  const findByKey = useFindByKey();

  const [status, setStatus] = useState<"idle" | "loading" | "error">("idle");

  const { mutate: updateOrderItem } = useUpdateOrderItem({
    onSuccess: () => {
      window.location.href = getPathnameForDestination({
        path: "/account/orders/" + customisation!.order_id,
        destination: "website",
      });
    },
    onError: (error) => {
      if (error.response?.status === 404) {
        // Didn't find the cart item. Best bet is to try adding to the cart afresh.
        setCustomisation({
          data: {
            ...customisation,
            cart_item_id: undefined,
            order_id: undefined,
          },
          behaviour: "replace",
        });

        router.push(getPathnameForDestination({ path: "/create/formats" }));
      } else {
        setStatus("error");
      }
    },
    retry: (failureCount, error) => {
      // Retry twice if it's not a `404`, just in case it's a very transient error
      if (error.response?.status && error.response.status === 404) {
        return false;
      }

      return failureCount < 2;
    },
  });

  const { mutate: updateCartItem } = useUpdateCartItem({
    onSuccess: () => {
      window.location.href = getPathnameForDestination({
        path: "/cart",
        destination: "website",
      });
    },
    onError: (error) => {
      if (error.response?.status === 404) {
        // Didn't find the cart item. Best bet is to try adding to the cart afresh.
        setCustomisation({
          data: { ...customisation, cart_item_id: undefined },
          behaviour: "replace",
        });

        router.push(getPathnameForDestination({ path: "/create/formats" }));
      } else {
        setStatus("error");
      }
    },
    retry: (failureCount, error) => {
      // Retry twice if it's not a `404`, just in case it's a very transient error
      if (error.response?.status && error.response.status === 404) {
        return false;
      }

      return failureCount < 2;
    },
  });

  const [activeDrawerModal, setActiveDrawerModal] =
    useState<FormDocument | null>(null);

  if (!product) {
    throw SSR_CONFIGURATION_ERROR;
  }

  useReCropPhotosOnSchemaChange();

  function hasIncompleteDrawers() {
    if (!product) {
      throw SSR_CONFIGURATION_ERROR;
    }

    return product.content.product.drawers.some((drawer) => {
      // Check that all drawer `muse_schema_key`s have been set
      if (drawer.muse_schema_key) {
        // The name of this function is the same of a proprietaty testing library
        // function so we we need to disable the eslint rule
        // eslint-disable-next-line testing-library/await-async-query
        const field = findByKey(drawer.muse_schema_key);

        if (field && _get(customisation, field.urlKey, false) === false) {
          const form = (drawer as any).form as FormDocument;

          // If not, open its modal for the user to make a selection
          setActiveDrawerModal(form);

          // And trigger a pageview
          if (form.data.tracking_id) {
            pageview(router.asPath + "#" + form.data.tracking_id);
          }

          return true;
        }
      }
    });
  }

  // If the critical form toggle in the Product Doc is set to true,
  // the user is prompted to interact with the in-page edit
  function hasIncompleteEssentialForms() {
    if (!product) {
      throw SSR_CONFIGURATION_ERROR;
    }

    return (
      product.content.product.in_page_edits
        // Make sure to only include pages that are visible to the user
        .filter(({ muse_page_id }) =>
          (toc || []).find(({ info }) => info?.pageName === muse_page_id)
        )
        .some((inPageEdit) => {
          if (inPageEdit.is_form_essential) {
            // We only need to check if the first field in this form has a value set
            const slice = ((inPageEdit as any).form as FormDocument).data
              .slices[0];
            if (
              slice &&
              "muse_schema_key" in slice.primary &&
              slice.primary.muse_schema_key
            ) {
              // The name of this function is the same of a proprietaty testing library
              // function so we we need to disable the eslint rule
              // eslint-disable-next-line testing-library/await-async-query
              const field = findByKey(slice.primary.muse_schema_key);

              if (field && _get(customisation, field.urlKey, false) === false) {
                const form = (inPageEdit as any).form as FormDocument;
                // If not, open its modal for the user to make a selection
                setActiveDrawerModal(form);

                // And trigger a pageview
                if (form.data.tracking_id) {
                  pageview(router.asPath + "#" + form.data.tracking_id);
                }

                return true;
              }
            }
          }
        })
    );
  }

  function onDrawerClicked(form: FormDocument) {
    if (form.data.tracking_id) {
      // If we have a page with the same id, scroll it into view
      document.getElementById(form.data.tracking_id)?.scrollIntoView();

      // Manually set the pageview to avoid faffing with hashes in the URL
      pageview(router.asPath + "#" + form.data.tracking_id);
    }

    setActiveDrawerModal(form);
  }

  function onDrawerDismiss(data?: qs.ParsedQs) {
    setActiveDrawerModal(null);
    if (data) {
      const priceDetail = getPageUpsellPrice(
        data,
        product as UseGetProductResponse
      );
      if (priceDetail) {
        updateSubtotal(priceDetail);
      }
    }

    // We track when a form is opened, so track when it closes too
    pageview(router.asPath);
  }

  async function onCtaClicked(
    event: ReactMouseEvent<HTMLButtonElement, MouseEvent>
  ) {
    // TODO: validate all required fields too
    if (
      !product ||
      !customisation ||
      hasIncompleteDrawers() ||
      hasIncompleteEssentialForms()
    ) {
      event.preventDefault();
      return;
    }

    setStatus("loading");

    if (!csrfToken) {
      throw MISSING_ESSENTIAL_DATA_ERROR;
    }

    // If the product uses the PDC formats page, redirect to that
    if (customisation.order_id && customisation.cart_item_id) {
      // We're editing from an order. Will skip formats and go straight to cart.
      updateOrderItem({
        csrfToken,
        customisation,
        cartItemId: customisation.cart_item_id as string,
      });
    } else if (customisation.cart_item_id) {
      // We're editing a cart item that hasn't been ordered yet.
      // Will update the item before sending back to cart
      updateCartItem({
        csrfToken,
        customisation,
        productSku: product.productSku,
        cartItemId: customisation.cart_item_id as string,
      });
    } else {
      // We're creating the book for the first time.
      // Send to regular Formats page
      router.push(getPathnameForDestination({ path: "/create/formats" }));
    }
  }

  if (tocError) {
    return <PreviewTocError error={tocError} />;
  } else if (customisationError) {
    return <PreviewTocError error={customisationError} />;
  }

  if (schemaIsLoading || !customisation) {
    return <PreviewSkeleton />;
  }

  return (
    <>
      <main className={styles.main}>
        <div className={styles.pagesContainer}>
          <Pages />
        </div>

        <div className={styles.bottomBarWrapper}>
          <div className={styles.bottomBar}>
            <Drawers onDrawerClicked={onDrawerClicked} />

            <div className={styles.cta}>
              <PrimaryButton
                as="button"
                className={styles.ctaButton}
                isLoading={status === "loading"}
                onClick={onCtaClicked}
                testId="preview-continue-cta"
              >
                {status === "loading" &&
                  interpolate(
                    product.content.canvas.form_loading_label ??
                      CANVAS_FALLBACK_CONTENT.form_loading_label
                  )}

                {status !== "loading" &&
                  interpolate(
                    product.content.canvas.form_submit_button_label ??
                      CANVAS_FALLBACK_CONTENT.form_submit_button_label
                  )}
              </PrimaryButton>
            </div>
          </div>
        </div>
      </main>

      {activeDrawerModal && (
        <SchemaFormModal
          formDocument={activeDrawerModal}
          isOpen={!!activeDrawerModal}
          onDismiss={(data) => onDrawerDismiss(data as qs.ParsedQs)}
          title="Edit page"
        />
      )}
    </>
  );
}

function PreviewTocError({
  error,
}: {
  error: AxiosError<TocError, any> | Error;
}) {
  const router = useRouter();

  const { data: product } = useGetProduct();
  const { data: customisation } = useGetCustomisation();

  const getPathnameForDestination = useGetPathnameForDestination();
  const interpolate = useInterpolate();

  return error instanceof AxiosError && Array.isArray(error.response?.data) ? (
    <GenericError
      error={Error(error.response?.data.map((d: any) => d.path[0]).join(", "))}
      fullPage
      title="We're missing some information"
      action={
        <PrimaryButton
          as="a"
          href={getPathnameForDestination({
            path: "/create/start",
            query: {
              locale:
                (customisation?.locale as string) ??
                // ^ Use the existing `locale` if we have one
                getLocaleFromMarketplace(router.query.marketplace as string),
            },
          })}
        >
          {interpolate(
            product?.content.canvas.start_again_button_label ??
              CANVAS_FALLBACK_CONTENT.start_again_button_label
          )}
        </PrimaryButton>
      }
      subtitle={
        <>
          Sorry, some data required to preview your book is missing.
          <br />
          In case anyone asks, we need:
        </>
      }
    />
  ) : (
    <GenericError error={Error(error.message)} fullPage />
  );
}
